import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { PublicClientApplication, InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser'
import { msalConfig, loginRequest, apiScopes, isDevMode } from './authConfig'

interface AuthUser {
  name: string
  email: string
  id: string
}

interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string>
}

const AuthContext = createContext<AuthContextType | null>(null)

const devUser: AuthUser = {
  name: 'Dev User',
  email: 'dev@localhost',
  id: 'dev-user-id',
}

let msalInstance: PublicClientApplication | null = null

if (!isDevMode) {
  msalInstance = new PublicClientApplication(msalConfig)
}

function accountToUser(account: AccountInfo): AuthUser {
  return {
    name: account.name || account.username,
    email: account.username,
    id: account.localAccountId,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(isDevMode ? devUser : null)
  const [isLoading, setIsLoading] = useState(!isDevMode)

  useEffect(() => {
    if (isDevMode) return

    const init = async () => {
      try {
        await msalInstance!.initialize()
        const response = await msalInstance!.handleRedirectPromise()
        if (response?.account) {
          msalInstance!.setActiveAccount(response.account)
          setUser(accountToUser(response.account))
        } else {
          const accounts = msalInstance!.getAllAccounts()
          if (accounts.length > 0) {
            msalInstance!.setActiveAccount(accounts[0]!)
            setUser(accountToUser(accounts[0]!))
          }
        }
      } catch (err) {
        console.error('MSAL init error:', err)
      } finally {
        setIsLoading(false)
      }
    }

    init()
  }, [])

  const login = useCallback(async () => {
    if (isDevMode) {
      setUser(devUser)
      return
    }
    try {
      await msalInstance!.loginRedirect(loginRequest)
    } catch (err) {
      console.error('Login error:', err)
    }
  }, [])

  const logout = useCallback(async () => {
    if (isDevMode) {
      setUser(null)
      return
    }
    try {
      await msalInstance!.logoutRedirect({ postLogoutRedirectUri: window.location.origin })
    } catch (err) {
      console.error('Logout error:', err)
    }
  }, [])

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (isDevMode) return 'dev-token'

    const account = msalInstance!.getActiveAccount()
    if (!account) throw new Error('No active account')

    try {
      const response = await msalInstance!.acquireTokenSilent({
        scopes: apiScopes,
        account,
      })
      return response.accessToken
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await msalInstance!.acquireTokenRedirect({ scopes: apiScopes })
        return ''
      }
      throw err
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
